import { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useAuthStore } from '@/stores/authStore'
import { UserRole } from '@/types/auth'
import { Loader2 } from 'lucide-react'

interface ProtectedRouteProps {
  children: ReactNode
  requiredPermission?: string
  requiredRole?: UserRole | UserRole[]
  fallback?: ReactNode
}

/**
 * ProtectedRoute component that wraps routes requiring authentication
 * and optionally specific permissions or roles
 */
export function ProtectedRoute({
  children,
  requiredPermission,
  requiredRole,
  fallback,
}: ProtectedRouteProps) {
  const location = useLocation()
  const { isAuthenticated, isLoading, hasPermission, hasRole } = useAuth()
  const isInitialized = useAuthStore((s) => s._initialized)
  const hasToken = useAuthStore((s) => !!s.token)

  // Show loading state while checking auth or waiting for token validation.
  // This prevents a race where persisted (stale) user data is used before
  // /auth/me confirms the actual role, causing wrong route redirects.
  //
  // Gated on isAuthenticated, NOT hasToken. An SSO session is cookie-only and
  // never populates state.token, so a hasToken gate skipped precisely the
  // session type most exposed to this race: with a stale persisted
  // {isAuthenticated: true, user: <previous person>}, navigating straight to a
  // protected URL fell through to the isAuthenticated check and rendered the
  // previous user's name and role-gated chrome until /auth/me corrected it.
  // Data stays governed by the HttpOnly cookie, but showing one person another
  // person's identity is exactly the confusion this whole change exists to end.
  if (isLoading || ((hasToken || isAuthenticated) && !isInitialized)) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Check permission if required
  if (requiredPermission && !hasPermission(requiredPermission)) {
    if (fallback) {
      return <>{fallback}</>
    }
    return <AccessDenied reason="permission" />
  }

  // Check role if required
  if (requiredRole && !hasRole(requiredRole)) {
    if (fallback) {
      return <>{fallback}</>
    }
    return <AccessDenied reason="role" />
  }

  return <>{children}</>
}

interface AccessDeniedProps {
  reason: 'permission' | 'role'
}

function AccessDenied({ reason }: AccessDeniedProps) {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900">403</h1>
        <h2 className="mt-2 text-xl font-semibold text-gray-700">Access Denied</h2>
        <p className="mt-4 text-gray-600">
          {reason === 'permission'
            ? "You don't have permission to access this resource."
            : "Your role doesn't allow access to this resource."}
        </p>
        <a
          href="/"
          className="mt-6 inline-block text-sm font-medium text-primary-600 hover:text-primary-500"
        >
          Return to Dashboard
        </a>
      </div>
    </div>
  )
}
